import { cloneDeep } from 'lodash-es';

import { compareStatGroups } from '../../utils/compare-statgroups';

import type { Flow, StatGroup } from '../../services/viva/constants';
import type { AddOnItem } from '../../components/BuyTickets';
import type {
  CheckoutAddonSelections,
  CheckoutType,
  OrderAttributes,
  ShopCart,
} from '../types';

export const addOnUpdateDTO = ({
  addOns,
  dateTime,
  originalDateTime,
  flow,
  data,
}: {
  addOns: AddOnItem[];
  dateTime: number;
  originalDateTime?: number;
  flow: Flow;
  data: CheckoutAddonSelections;
}): CheckoutAddonSelections => {
  const output = { ...data };

  // If originalDateTime is set, remove the addOns from that date
  // we're editing an existing selection
  // if (originalDateTime && output[flow]?.[originalDateTime]) {
  //   delete output[flow][originalDateTime];
  // }
  // For now we are just starting over with Addons in this
  delete output[flow];

  if (!output[flow]) {
    output[flow] = {};
  }

  if (!output[flow][dateTime]) {
    output[flow][dateTime] = [];
  }

  const nonZeroAddons = addOns.filter((addOn) => {
    return (addOn.quantity ?? 0) > 0;
  });

  output[flow][dateTime] = nonZeroAddons;

  return output;
};

export const setFlowSelectionDTO = ({
  flow,
  data,
  performances,
  dateTime,
  state: _state,
}: {
  flow: Flow;
  data: CheckoutType['cart']['selections'][0];
  performances: ShopCart['performances'];
  dateTime: number;
  state: CheckoutType;
}): CheckoutType | null => {
  const state = cloneDeep(_state);

  if (data) {
    state.cart.selections[flow] = data.filter((sel) => sel.count > 0);
  }

  if (performances) {
    if (!state.cart.performances[flow]) {
      state.cart.performances[flow] = {};
    }

    // Update to keep only the newest performance
    const newPerformances = performances[flow];

    const newData = {
      ...state.cart.performances,
      [flow]: {
        ...state.cart.performances[flow],
        [dateTime]: newPerformances,
      },
    };
    state.cart.performances = newData;

    return state;
  }

  return null;
};

export const removeByTypeAndDateTimeDTO = ({
  type,
  dateTime,
  flow,
  state,
}: {
  type: string;
  dateTime: number;
  flow: Flow;
  state: CheckoutType;
}) => {
  const newState = { ...state };
  const flowSelections = newState.cart.selections[flow];

  if (!flowSelections) return newState;

  // Remove the selection
  newState.cart.selections[flow] = flowSelections.filter(
    (selection) =>
      !(selection.type === type && selection.dateTime === dateTime),
  );

  // Update add-ons
  const addOnsForFlow = newState.cart.addOnSelections?.[flow]?.[dateTime];
  if (addOnsForFlow && flowSelections.length > 0) {
    const selectionTotal = flowSelections
      .filter((selection) => selection.dateTime === dateTime)
      .reduce((acc, selection) => acc + selection.count, 0);

    newState.cart.addOnSelections[flow][dateTime] = addOnsForFlow
      .map((addOn) => ({
        ...addOn,
        quantity: Math.max(Math.min(addOn.quantity ?? 0, selectionTotal), 0),
      }))
      .filter((addOn) => (addOn.quantity ?? 0) > 0);
  }

  return newState;
};

export const clearDateFromCheckoutDTO = ({
  flow,
  dateTime,
  state,
}: {
  flow: Flow;
  dateTime: number;
  state: CheckoutType;
}) => {
  const newState = { ...state };
  const flowSelections = newState.cart.selections[flow];

  if (flowSelections) {
    newState.cart.selections[flow] = flowSelections.filter(
      (sel) => sel.dateTime !== dateTime,
    );
  }

  // Remove performances
  if (newState.cart.performances[flow]) {
    delete newState.cart.performances[flow][dateTime];
  }

  // Remove addons
  if (newState.cart.addOnSelections[flow]) {
    delete newState.cart.addOnSelections[flow][dateTime];
  }

  return newState;
};

export const removeAddOnDTO = ({
  flow,
  dateTime,
  statGroup,
  state: _state,
}: {
  flow: Flow;
  dateTime: number;
  statGroup: StatGroup;
  state: CheckoutType;
}) => {
  const state = { ..._state };

  const addOnIndex = state.cart.addOnSelections[flow][dateTime].findIndex(
    (addOn) => compareStatGroups(addOn.statGroup, statGroup),
  );

  if (addOnIndex !== -1) {
    state.cart.addOnSelections[flow][dateTime].splice(addOnIndex, 1);
  }

  return state;
};

export const setAttributesDTO = ({
  attributes,
  state: _state,
}: {
  attributes: OrderAttributes;
  state: CheckoutType;
}) => {
  const state = { ..._state };

  state.attributes = {
    ...state.attributes,
    ...attributes,
  };

  return state;
};
